import React, { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import ApplicationContainer from '../../state/containers/ApplicationContainer';
import { FormikProps } from 'formik';
import Button from '../../components/general/Button';
import HorizontalContainer from '../../components/structure/HorizontalContainer';
import ImageUploadContainer from '../../components/structure/ImageUploadContainer';
import AvatarFileUploadField from '../../components/forms/AvatarFileUploadField';
import FormFieldsContainer from '../../components/structure/FormFieldsContainer';
import TextField from '../../components/forms/TextField';
import TextAreaField from '../../components/forms/TextAreaField';
import ApplicationModel from '../../models/applications/api/ApplicationModel';
import ModalTitle from '../../components/general/ModalTitle';
import ApplicationEnvironmentsContainer, { ApplicationEnvironmentsOption } from '../../state/containers/ApplicationEnvironmentsContainer';
import ApplicationEnvironmentModel from '../../models/applications/api/environments/ApplicationEnvironmentModel';
import CheckboxField from '../../components/forms/CheckboxField';
import Switch from '../../components/forms/Switch';
import ContentCollapsingSection from '../../components/structure/ContentCollapsingSection';
import styled from '@emotion/styled';
import StyledDropdown from '../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import { color_text_default, light_black } from '../../constants/colors';
import { ADORA, OLO, PAYTRONIX, SPEEDLINE, TOAST } from '../../constants/posNames';
import moment from "moment-timezone";
import * as applicationsApi from '../../api/controllers/applications';

const advancedSettingsIcon = require('../../content/images/application-settings/advanced-settings.svg');
const importIcon = require('../../content/images/refresh.svg');

export interface ApplicationInformationFormProps {
    appContainer: ApplicationContainer;
    app: ApplicationModel;
    languageIds?: string[];
    environmentOption?: ApplicationEnvironmentsOption;
    environment?: ApplicationEnvironmentModel;
    environmentsContainer: ApplicationEnvironmentsContainer;
    imageUrl?: string;
}

export interface ApplicationInformationFormData {
    name?: string
    invocationPhrase?: string
    shortDescription?: string
    description?: string
    keywords?: string
    timeZone?: string,
    posConfig?: string,
    imageFile: File
    imageItemId?: string
    featureFlags?: FeatureFlagModel[]
    selectedFlagIds?: string[],
    manageMenuApplicationId?: string,
    posName?: string,
    posLocationId?: string,
    outboundCallLocationId?: string,
    outboundCallIntegrationName?: string,
    isLive?: boolean,
}
const posNameOptions = [
    { label: "Adora", value: ADORA },
    { label: "Custom", value: "Custom" },
    { label: "Olo", value: OLO },
    { label: "Paytronix", value: PAYTRONIX },
    { label: "Speedline", value: SPEEDLINE },
    { label: "Toast", value: TOAST },
]

const OutboundCallIntegrationOptions = [
    { label: "Denticon", value: "Denticon" },
    { label: "Dentrix Ascend", value: "DentrixAscend" },
    { label: "OpenDental", value: "OpenDental" },
    { label: "Epic", value: "Epic" },
    { label: "Salesforce", value: "Salesforce" }
]
const ApplicationInformationForm: React.FC<ApplicationInformationFormProps & FormikProps<ApplicationInformationFormData>> = (props) => {

    const [imageUrl, setImageUrl] = useState(props.app.imageUrl);
    const [isPosConfigLoading, setIsPosConfigLoading] = useState(false);

    useEffect(() => {
        props.appContainer.loadFeatureFlags();
        // set default pos name value if one isn't saved yet
        if (!props.app.posName)
            props.setFieldValue("posName", "SPEEDLINE");

        if (props.appContainer.state.posConfig) {
            props.setFieldValue("posConfig", props.appContainer.state.posConfig);
        }
    }, []);

    const isLoading = props.appContainer.state.isCreatingApplication;
    const allFeatureFlags = props.appContainer.state.featureFlags;

    const handleLoadPosConfig = async () => {
        if (!props.app.id) return;

        setIsPosConfigLoading(true);
        await props.appContainer.loadPOSConfig(props.app.id);
        props.setFieldValue("posConfig", props.appContainer.state.posConfig);
        setIsPosConfigLoading(false);
    };

    const handleFeatureFlagsChange = (flag: FeatureFlagModel) => {
        // manage check box
        const flagsCopy = [...props.values.selectedFlagIds];
        if (flagsCopy.includes(flag.id)) {
            flagsCopy.splice(flagsCopy.indexOf(flag.id), 1);
        } else {
            flagsCopy.push(flag.id);
        }
        props.setFieldValue("selectedFlagIds", flagsCopy);

    };

    const handlePOSNameChange = (optionValue: string, value: string | boolean) => {
        props.setFieldValue(optionValue, value);
    };
    const handleOutboundCallIntegrationNameChange = (optionValue: string, value: string | boolean) => {
        props.setFieldValue(optionValue, value);
    };
    const handleFoodOrderingGenerativeAIModelChange = (optionValue: string, value: string | boolean) => {
        const featureFlag = props.values.featureFlags?.find(f => f.name === optionValue);
        // featureFlag.GenerativeAIModel = value as string;
        props.setFieldValue("featureFlags", [...props.values.featureFlags]);
    };

    const usingCustomPOS = props.values.posName === "Custom" || !posNameOptions.some(o => o.value === props.values.posName);
    const renderFeatureFlags = () => {
        return allFeatureFlags.map((flag, index) => {
            return (
                <>
                    <CheckboxField
                        key={`KEY ${index}`}
                        name={flag.name}
                        label={flag.name}
                        labelStyle={checkboxLabelStyle}
                        disabled={isLoading}
                        checked={props.values.selectedFlagIds.includes(flag.id)}
                        onChange={() => handleFeatureFlagsChange(flag)}
                        className={checkboxField}
                    />
                    {
                        flag.name == "Food Ordering" &&
                        props.values.selectedFlagIds.includes(flag.id) &&
                        <AdvancedSettingsFieldsWrapper>

                            <TextField
                                name="posLocationId"
                                disabled={isLoading}
                                value={props.values.posLocationId}
                                label="Location ID"
                                placeholder="The location ID in the POS"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <CustomDropdown
                                label="POS Name"
                                disabled={isLoading}
                                handleOnChange={handlePOSNameChange}
                                fieldName="posName"
                                options={posNameOptions}
                                size="medium"
                                searchable={true}
                                value={usingCustomPOS ? "Custom" : props.values.posName}
                            />
                            {
                                usingCustomPOS &&
                                <TextField
                                    name="posName"
                                    disabled={isLoading}
                                    value={props.values.posName}
                                    label="Custom POS Name"
                                    placeholder="The name of the POS"
                                    onChange={props.handleChange}
                                    onBlur={props.handleBlur}
                                />
                            }
                            <TextField
                                name="manageMenuApplicationId"
                                disabled={isLoading}
                                value={props.values.manageMenuApplicationId}
                                label="Manage Menu Application ID"
                                placeholder="The Id of the Voicify application that manages the menu"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                        </AdvancedSettingsFieldsWrapper>
                    }
                    {
                        flag.name == "Outbound Calling" &&
                        props.values.selectedFlagIds.includes(flag.id) &&
                        <AdvancedSettingsFieldsWrapper>

                            <TextField
                                name="outboundCallLocationId"
                                disabled={isLoading}
                                value={props.values.outboundCallLocationId}
                                label="Location ID"
                                placeholder="The location ID or office ID"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <CustomDropdown
                                label="Integration"
                                disabled={isLoading}
                                handleOnChange={handleOutboundCallIntegrationNameChange}
                                fieldName="outboundCallIntegrationName"
                                options={OutboundCallIntegrationOptions}
                                size="medium"
                                searchable={true}
                                value={props.values.outboundCallIntegrationName}
                            />
                        </AdvancedSettingsFieldsWrapper>
                    }
                </>
            )
        });

    };

    const readDataUrl = () => {
        const reader = new FileReader();
        reader.onloadend = e => {
            setImageUrl(reader.result.toString())
        };
    };

    const handleImageSelected = (file: File) => {
        readDataUrl();
        props.setFieldValue("imageFile", file);
    };


    return (
        <form onSubmit={props.handleSubmit}>
            <ModalTitle>App Info</ModalTitle>
            <HorizontalContainer>
                <FormFieldsContainer className={borderedFields}>
                    <NormalSettingsWrapper>
                        <p className="detail-label">
                            Here you can manage the public-facing aspects of your application.
                        </p>
                        <p className="detail-label">
                            Application Name is how you identify your app. Invocation Name is the phrase people will use to call up your application on their device
                        </p>
                        <TextField
                            name="name"
                            disabled={isLoading}
                            required
                            value={props.values.name}
                            label="Application Name:"
                            placeholder="Give your application a name"
                            onChange={props.handleChange}
                            onBlur={props.handleBlur}
                        />
                        <TextField
                            name="invocationPhrase"
                            disabled={isLoading}
                            tooltip="Invocation Name is the phrase people will use to call up your application on their device"
                            required
                            value={props.values.invocationPhrase}
                            label="Invocation Name:"
                            placeholder="The name used to start your app"
                            onChange={props.handleChange}
                            onBlur={props.handleBlur}
                        />
                        <TextField
                            name="shortDescription"
                            disabled={isLoading}
                            value={props.values.shortDescription}
                            label="Short Description:"
                            placeholder="Just something brief"
                            onChange={props.handleChange}
                            onBlur={props.handleBlur}
                        />
                        <TextAreaField
                            name="description"
                            disabled={isLoading}
                            value={props.values.description}
                            label="Detailed Description:"
                            placeholder="Now with more detail"
                            onChange={props.handleChange}
                            onBlur={props.handleBlur}
                        />
                        <TextField
                            name="keywords"
                            disabled={isLoading}
                            value={props.values.keywords}
                            label="Keywords:"
                            placeholder="These can help with finding your app/skill"
                            onChange={props.handleChange}
                            onBlur={props.handleBlur}
                        />
                        <CustomDropdown
                            label="Time Zone"
                            disabled={isLoading}
                            handleOnChange={props.setFieldValue}
                            fieldName="timeZone"
                            options={moment.tz.names().map(timezone => ({ value: timezone, label: timezone }))}
                            size="medium"
                            searchable={true}
                            value={props.values.timeZone}
                        />
                        <PosConfigContainer>
                            <label className="pos-config-label">
                                POS Configuration:
                            </label>
                            <Button
                                type="button"
                                themes={["secondary-small"]}
                                icon={importIcon}
                                text={isPosConfigLoading ? "Loading..." : "Load POS Config"}
                                disabled={isLoading || isPosConfigLoading}
                                loading={isPosConfigLoading}
                                onClick={handleLoadPosConfig}
                                className="pos-config-button"
                            />
                            {props.values.posConfig != null && (
                                <>
                                    {props.values.posName === SPEEDLINE && (
                                        <PosConfigNote>
                                            Note: The config does not show the world pay credentials
                                        </PosConfigNote>
                                    )}

                                    <TextAreaField
                                        name="posConfig"
                                        disabled={isLoading}
                                        value={props.values.posConfig}
                                        label=""
                                        placeholder="Enter POS configuration (JSON format)"
                                        className="field_value"
                                        onChange={props.handleChange}
                                    />
                                </>
                            )}
                        </PosConfigContainer>
                    </NormalSettingsWrapper>

                    <ContentCollapsingSection
                        title="Advanced Options"
                        icon={advancedSettingsIcon}
                        isCollapsedByDefault={true}
                        className={collapsedSection}
                        bottom
                    >
                        {renderFeatureFlags()}
                        <Switch
                            checked={props.values.isLive ?? false}
                            onChange={(enabled) => props.setFieldValue('isLive', enabled)}
                            label="Go Live"
                        />
                    </ContentCollapsingSection>
                    <div className={saveButtonWrapperStyle}>
                        <Button
                            loading={isLoading}
                            disabled={isLoading}
                            themes={["primary"]}
                            text="Save"
                            type="submit"
                        />
                        {props?.status?.message && (
                            <StatusMessage>{props.status.message}</StatusMessage>
                        )}
                    </div>
                </FormFieldsContainer>
                <ImageUploadContainer>
                    <label>App Avatar</label>
                    <AvatarFileUploadField
                        imageUrl={imageUrl}
                        accept=".png"
                        onChange={handleImageSelected.bind(this)}
                    />
                </ImageUploadContainer>
            </HorizontalContainer>
        </form>
    )
}

const CustomDropdown = styled(StyledDropdown)`
    margin: 8px 0 26px 0;
`;

const PosConfigContainer = styled.div`
    .pos-config-label {
        margin-bottom: 12px;
        display: block;
        font-size: 14px;
        font-weight: 500;
        color: ${color_text_default};
    }

    .pos-config-button {
        margin: 0 0 16px 0 !important;

        .left-img {
            width: 16px;
            height: 16px;
            margin-right: 8px;
        }

        &:hover {
            transform: translateY(-1px);
            transition: transform 0.2s ease;
        }

        &:active {
            transform: translateY(0);
        }
    }
`;

const PosConfigNote = styled.div`
    margin-top: 8px;
    font-size: 14px;
    color: #666;
`;

const StatusMessage = styled.div`
    width: 30%;
    margin: auto;
    margin-top: 20px;
    padding: 10px;
    border-radius: 4px;
    background-color: ${light_black};
    color: ${color_text_default};
    font-size: 14px;
    text-align: center;
`;

const checkboxField = css`
    padding: 0 24px;
`;

const checkboxLabelStyle = css`
    font-size: 16px;
`;

const NormalSettingsWrapper = styled.div`
    padding-right: 64px;

    .field_value {
        width: 100%;
        margin-top: 14px;
        label {
            margin-left: 6px;
        }
        textarea {
            overflow-y: auto;
        }

        /* Special styling for POS config field */
        &[name="posConfig"] textarea {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            min-height: 150px;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            line-height: 1.4;
        }
    }
`;

const AdvancedSettingsFieldsWrapper = styled.div`
    padding-right: 34px;
    margin: auto;
    margin-left: 60px;
`;

const collapsedSection = css`
    margin-left: -50px;
    *.card-title-container {
        padding: 24px 24px 24px 40px;
    }
`;

const borderedFields = css`
    border-right: 1px solid #c7cfd2;
    margin-top: 0;

    .detail-label {
        font-family: Muli;
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: 0.5px;
        color: #8e97a1;
        margin-bottom: 16px;
    }
`;

const saveButtonWrapperStyle = css`
    display: flex;
`;
export default ApplicationInformationForm
